// SkinExpert/src/app/services/AccountService.ts -- similar 
import AuthService from "./AuthService";

const authService = new AuthService();

export interface UserProfile {
  id: string;
  email: string;
  userName: string;
  emailConfirmed: boolean;
  phoneNumber?: string;
  isApproved: boolean;
  roles: string[];
  twoFactorEnabled: boolean;
}

export interface UpdateProfileData {
  userName?: string;
  phoneNumber?: string;
}

export interface ChangeEmailData {
  newEmail: string;
  currentPassword: string;
}

export interface ChangePasswordData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export interface CreateTeamMemberData {
  email: string;
  password: string;
}

export interface TeamMember {
  id: string;
  email: string;
  userName: string;
  phoneNumber?: string;
  createdAt: string;
  subscriptionStatus?: string;
  planType?: string;
}

export interface TeamMembersResponse {
  teamMembers: TeamMember[];
  maxMembers: number;
  currentCount: number;
}

const IDENTITY_API = process.env.REACT_APP_AUTHORITY;

class AccountService {
  private async getHeaders(): Promise<HeadersInit> {
    const token = await authService.getAccessToken();
    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
  }

async getProfile(): Promise<UserProfile> {
  const headers = await this.getHeaders();
  console.log("Fetching profile with headers:", headers);
  
  const response = await fetch(`${IDENTITY_API}/api/account/profile`, {
    method: "GET",
    headers,
  });

  console.log("Response status:", response.status);
  console.log("Response headers:", response.headers.get('content-type'));

  if (!response.ok) {
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      const error = await response.json();
      throw new Error(error.message || `HTTP ${response.status}`);
    } else {
      const errorText = await response.text();
      console.error("Error response (HTML):", errorText);
      throw new Error(`HTTP ${response.status}: ${errorText.substring(0, 200)}`);
    }
  }

  return response.json();
}

async updateProfile(data: UpdateProfileData): Promise<{ message: string }> {
    const headers = await this.getHeaders();
    const response = await fetch(`${IDENTITY_API}/api/account/profile`, {
      method: "PUT",
      headers,
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to update profile");
    }

    return response.json();
  }

  async changeEmail(data: ChangeEmailData): Promise<{ message: string }> {
    const headers = await this.getHeaders();
    const response = await fetch(`${IDENTITY_API}/api/account/email`, {
      method: "PUT",
      headers,
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to change email");
    }

    return response.json();
  }

  async changePassword(data: ChangePasswordData): Promise<{ message: string }> {
    const headers = await this.getHeaders();
    const response = await fetch(`${IDENTITY_API}/api/account/password`, {
      method: "PUT",
      headers,
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to change password");
    }

    return response.json();
  }

  async deleteAccount(password: string): Promise<{ message: string }> {
    const headers = await this.getHeaders();
    const response = await fetch(`${IDENTITY_API}/api/account`, {
      method: "DELETE",
      headers,
      body: JSON.stringify({ password }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to delete account");
    }

    return response.json();
  }

  async createTeamMember(data: CreateTeamMemberData): Promise<{ message: string; teamMember: TeamMember }> {
    const headers = await this.getHeaders();
    const response = await fetch(`${IDENTITY_API}/api/account/team-members`, {
      method: "POST",
      headers,
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to create team member");
    }

    return response.json();
  }

  async getTeamMembers(): Promise<TeamMembersResponse> {
    const headers = await this.getHeaders();
    const response = await fetch(`${IDENTITY_API}/api/account/team-members`, {
      method: "GET",
      headers,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to get team members");
    }

    return response.json();
  }

  async deleteTeamMember(id: string): Promise<{ message: string }> {
    const headers = await this.getHeaders();
    const response = await fetch(`${IDENTITY_API}/api/account/team-members/${id}`, {
      method: "DELETE",
      headers,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to delete team member");
    }

    return response.json();
  }
}

export default new AccountService();